/**
 * Workout plan type enumeration.
 */
export var WorkoutPlanType;
(function (WorkoutPlanType) {
    WorkoutPlanType["AI_SUGGESTED"] = "AI_SUGGESTED";
    WorkoutPlanType["CUSTOM"] = "CUSTOM";
    WorkoutPlanType["KOL_ROUTINE"] = "KOL_ROUTINE";
    WorkoutPlanType["GOAL_ROUTINE"] = "GOAL_ROUTINE";
    WorkoutPlanType["SEVEN_DAY"] = "SEVEN_DAY";
    WorkoutPlanType["QUICK_LOG"] = "QUICK_LOG";
})(WorkoutPlanType || (WorkoutPlanType = {}));
/**
 * Source of workout log entry.
 */
export var WorkoutLogSource;
(function (WorkoutLogSource) {
    WorkoutLogSource["MANUAL"] = "MANUAL";
    WorkoutLogSource["SCAN"] = "SCAN";
    WorkoutLogSource["PLAN"] = "PLAN";
})(WorkoutLogSource || (WorkoutLogSource = {}));
/**
 * Exercise difficulty level enumeration.
 */
export var ExerciseDifficultyLevel;
(function (ExerciseDifficultyLevel) {
    ExerciseDifficultyLevel["BEGINNER"] = "BEGINNER";
    ExerciseDifficultyLevel["INTERMEDIATE"] = "INTERMEDIATE";
    ExerciseDifficultyLevel["ADVANCED"] = "ADVANCED";
})(ExerciseDifficultyLevel || (ExerciseDifficultyLevel = {}));
/**
 * How workout sets are recorded for an exercise.
 */
export var ExerciseLoggingMode;
(function (ExerciseLoggingMode) {
    ExerciseLoggingMode["REPS_AND_WEIGHT"] = "REPS_AND_WEIGHT";
    ExerciseLoggingMode["DURATION"] = "DURATION";
})(ExerciseLoggingMode || (ExerciseLoggingMode = {}));
/**
 * Exercise Library movement pattern (FitCoach generation / Pattern coverage).
 * Values match the xlsx slugs (`FitCoach_Exercise_Library_資料結構_v1`).
 */
export var ExerciseMovementPattern;
(function (ExerciseMovementPattern) {
    ExerciseMovementPattern["SQUAT"] = "squat";
    ExerciseMovementPattern["HINGE"] = "hinge";
    ExerciseMovementPattern["HORIZONTAL_PUSH"] = "horizontal_push";
    ExerciseMovementPattern["VERTICAL_PUSH"] = "vertical_push";
    ExerciseMovementPattern["HORIZONTAL_PULL"] = "horizontal_pull";
    ExerciseMovementPattern["VERTICAL_PULL"] = "vertical_pull";
    ExerciseMovementPattern["LUNGE"] = "lunge";
    ExerciseMovementPattern["ISOLATION"] = "isolation";
    ExerciseMovementPattern["ROTATION"] = "rotation";
})(ExerciseMovementPattern || (ExerciseMovementPattern = {}));
/**
 * How a FitCoach exercise is measured for generation/filter logic
 * (3-value; `ExerciseLoggingMode` stays the 2-value UI logging mode).
 */
export var ExerciseMeasurementType;
(function (ExerciseMeasurementType) {
    ExerciseMeasurementType["REPS"] = "reps";
    ExerciseMeasurementType["HOLD"] = "hold";
    ExerciseMeasurementType["CARDIO"] = "cardio";
})(ExerciseMeasurementType || (ExerciseMeasurementType = {}));
/** Exercise type (FitCoach spec 6.4): strength / cardio / hiit / stretching. */
export var ExerciseType;
(function (ExerciseType) {
    ExerciseType["STRENGTH"] = "strength";
    ExerciseType["CARDIO"] = "cardio";
    ExerciseType["HIIT"] = "hiit";
    ExerciseType["STRETCHING"] = "stretching";
})(ExerciseType || (ExerciseType = {}));
/**
 * Focus Area 8-value set used for generation/filtering (lowercase slugs).
 * Coexists with the finer-grained `MuscleGroup` (display/search), Q5.
 */
export var FitCoachPrimaryArea;
(function (FitCoachPrimaryArea) {
    FitCoachPrimaryArea["FULL_BODY"] = "full_body";
    FitCoachPrimaryArea["CHEST"] = "chest";
    FitCoachPrimaryArea["BACK"] = "back";
    FitCoachPrimaryArea["SHOULDERS"] = "shoulders";
    FitCoachPrimaryArea["ARMS"] = "arms";
    FitCoachPrimaryArea["ABS"] = "abs";
    FitCoachPrimaryArea["LEGS"] = "legs";
    FitCoachPrimaryArea["GLUTES"] = "glutes";
})(FitCoachPrimaryArea || (FitCoachPrimaryArea = {}));
/**
 * Persistent-profile experience level (Onboarding sets once, Settings may
 * override; lowercase per prompt contract).
 */
export var FitnessExperienceLevel;
(function (FitnessExperienceLevel) {
    FitnessExperienceLevel["BEGINNER"] = "beginner";
    FitnessExperienceLevel["INTERMEDIATE"] = "intermediate";
    FitnessExperienceLevel["ADVANCED"] = "advanced";
})(FitnessExperienceLevel || (FitnessExperienceLevel = {}));
/**
 * Target muscle group enumeration.
 */
export var MuscleGroup;
(function (MuscleGroup) {
    MuscleGroup["CHEST"] = "CHEST";
    MuscleGroup["BACK"] = "BACK";
    MuscleGroup["SHOULDERS"] = "SHOULDERS";
    MuscleGroup["BICEPS"] = "BICEPS";
    MuscleGroup["TRICEPS"] = "TRICEPS";
    MuscleGroup["LEGS"] = "LEGS";
    MuscleGroup["GLUTES"] = "GLUTES";
    MuscleGroup["CORE"] = "CORE";
    MuscleGroup["FOREARMS"] = "FOREARMS";
    MuscleGroup["CALVES"] = "CALVES";
    MuscleGroup["FULL_BODY"] = "FULL_BODY";
})(MuscleGroup || (MuscleGroup = {}));
/**
 * Source of activity log entry.
 */
export var ActivityLogSource;
(function (ActivityLogSource) {
    ActivityLogSource["TIMER"] = "TIMER";
    ActivityLogSource["IMPORT"] = "IMPORT";
})(ActivityLogSource || (ActivityLogSource = {}));
/**
 * Activity log status.
 */
export var ActivityLogStatus;
(function (ActivityLogStatus) {
    ActivityLogStatus["DRAFT"] = "DRAFT";
    ActivityLogStatus["COMPLETED"] = "COMPLETED";
    ActivityLogStatus["DELETED"] = "DELETED";
})(ActivityLogStatus || (ActivityLogStatus = {}));
/**
 * Activity visibility for social features.
 */
export var ActivityVisibility;
(function (ActivityVisibility) {
    ActivityVisibility["PRIVATE"] = "PRIVATE";
    ActivityVisibility["FOLLOWERS"] = "FOLLOWERS";
    ActivityVisibility["EVERYONE"] = "EVERYONE";
})(ActivityVisibility || (ActivityVisibility = {}));
/**
 * Activity tags for categorization.
 */
export var ActivityTag;
(function (ActivityTag) {
    ActivityTag["WORKOUT"] = "WORKOUT";
    ActivityTag["RECOVERY"] = "RECOVERY";
    ActivityTag["COMMUTE"] = "COMMUTE";
    ActivityTag["WITH_KID"] = "WITH_KID";
})(ActivityTag || (ActivityTag = {}));
/**
 * Fields that can be hidden from public view.
 */
export var ActivityHiddenField;
(function (ActivityHiddenField) {
    ActivityHiddenField["CALORIES"] = "CALORIES";
    ActivityHiddenField["START_TIME"] = "START_TIME";
})(ActivityHiddenField || (ActivityHiddenField = {}));
/**
 * Perceived exertion zone derived from 1-10 score.
 */
export var PerceivedExertionZone;
(function (PerceivedExertionZone) {
    PerceivedExertionZone["EASY"] = "EASY";
    PerceivedExertionZone["MODERATE"] = "MODERATE";
    PerceivedExertionZone["MAX"] = "MAX";
})(PerceivedExertionZone || (PerceivedExertionZone = {}));
/**
 * FitCoach setup: location / equipment context.
 * Display order: Gym → Home (Basic) → Home (None) → Outdoor.
 */
export var FitCoachLocationEquipment;
(function (FitCoachLocationEquipment) {
    FitCoachLocationEquipment["GYM_FULL"] = "GYM_FULL";
    FitCoachLocationEquipment["HOME_BASIC"] = "HOME_BASIC";
    FitCoachLocationEquipment["HOME_NONE"] = "HOME_NONE";
    FitCoachLocationEquipment["OUTDOOR"] = "OUTDOOR";
})(FitCoachLocationEquipment || (FitCoachLocationEquipment = {}));
/**
 * FitCoach setup: body-region focus (single select).
 * Display order: Full Body → Chest → Back → Shoulders → Arms → Abs → Legs → Glutes.
 */
export var FitCoachFocusArea;
(function (FitCoachFocusArea) {
    FitCoachFocusArea["FULL_BODY"] = "FULL_BODY";
    FitCoachFocusArea["CHEST"] = "CHEST";
    FitCoachFocusArea["BACK"] = "BACK";
    FitCoachFocusArea["SHOULDERS"] = "SHOULDERS";
    FitCoachFocusArea["ARMS"] = "ARMS";
    FitCoachFocusArea["ABS"] = "ABS";
    FitCoachFocusArea["LEGS"] = "LEGS";
    FitCoachFocusArea["GLUTES"] = "GLUTES";
})(FitCoachFocusArea || (FitCoachFocusArea = {}));
/**
 * FitCoach setup: session intensity (wire field remains `feeling`).
 * Ordered low → high for conservative conflict resolution.
 */
export var FitCoachFeeling;
(function (FitCoachFeeling) {
    FitCoachFeeling["RECOVERY"] = "RECOVERY";
    FitCoachFeeling["LIGHT"] = "LIGHT";
    FitCoachFeeling["MODERATE"] = "MODERATE";
    FitCoachFeeling["INTENSE"] = "INTENSE";
})(FitCoachFeeling || (FitCoachFeeling = {}));
/** Intensity rank (lower = more conservative). */
export const FIT_COACH_INTENSITY_RANK = {
    [FitCoachFeeling.RECOVERY]: 0,
    [FitCoachFeeling.LIGHT]: 1,
    [FitCoachFeeling.MODERATE]: 2,
    [FitCoachFeeling.INTENSE]: 3,
};
/**
 * When user pick and data-derived tier conflict, keep the lower intensity
 * so we never push past what the user (or history) can safely handle.
 */
export function resolveFitCoachIntensity(user, suggested) {
    if (suggested == null)
        return user;
    return FIT_COACH_INTENSITY_RANK[user] <= FIT_COACH_INTENSITY_RANK[suggested]
        ? user
        : suggested;
}
/** Allowed FitCoach session durations (minutes). `60` is shown as 60+. */
export const FIT_COACH_TIME_MINUTES = [10, 20, 30, 45, 60];
